import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { MiniGameService } from '../../../_services/mini-game.service';
import { GameAudioService } from '../../../_services/game-audio.service';
import { ImmersiveGameBase } from '../_shared/immersive-game.base';
import { GlobalService } from 'src/app/_services/global.service';
import { MiniGameSession, MiniGameStudent } from '../../../interfaces/mini-game.interface';
import { VaultCrackerRound, VaultCrackerState } from '../../../interfaces/vault-cracker.interface';
import { VaultCrackerStageComponent } from './vault-cracker-stage.component';

/**
 * Host shell for Vault Cracker. Reads `VaultCrackerState` from
 * `MiniGameService.session$`, drives the `<app-vault-cracker-stage>` (via the
 * blue SET button → `stage.commit()`), and writes outcomes back through
 * `scoreVaultCrackerRound` + `advanceVaultCrackerRound`.
 *
 * Studio rules live here:
 *  - Turn-intro card (announces the student and gates the dial).
 *  - Productive struggle (§3.2): a wrong SET is NEVER a hard fail and plays NO
 *    buzzer. On each miss a misconception "trap" is classified, a scaffolded,
 *    answer-preserving hint (fraction ring + text) reveals at the first miss or
 *    after 15s idle, and the KIND COACH — Rocco the raccoon vault-keeper —
 *    fades in with a rotating growth-mindset line.
 *  - Skill analytics (§3.6): timing/tries come from `ImmersiveGameBase`; the
 *    trap hit, whether the content hint was used, and how the round was solved
 *    (clean / reasoned-retry / walked) are written onto the state and stamped
 *    onto the shared `gameWorld-round` event by the service.
 *
 * Rocco is a PERSISTENT element toggled purely by `showRocco` opacity (never
 * created/destroyed → no flicker); a repeat miss swaps the copy in place.
 *
 * Chrome: the shell renders its OWN bottom-right fullscreen button (`.fs-btn`)
 * matching the "Select Game" button (§3.7), not `<app-fullscreen-toggle>`.
 */
@Component({
  selector: 'app-vault-cracker-game',
  templateUrl: './vault-cracker-game.component.html',
  styleUrls: ['./vault-cracker-game.component.scss']
})
export class VaultCrackerGameComponent extends ImmersiveGameBase implements OnInit, OnDestroy {
  @ViewChild(VaultCrackerStageComponent) stage?: VaultCrackerStageComponent;

  private destroy$ = new Subject<void>();
  currLang: any = 'en';

  session: MiniGameSession | null = null;

  showSuccess = false;
  showTurnCard = false;
  resolving = false;
  lastXp = 0;

  // Scaffolded content hint (fraction ring + text) — appears after the struggle beat.
  hintVisible = false;
  hintText: { en: string; ar: string } = { en: '', ar: '' };

  // Solve-type tracking (fed to analytics via the state at solve time).
  private prevGuess: number | null = null;
  private fineBandLit = false;

  // ── Kind coach — Rocco the raccoon vault-keeper (theme §3.2) ──────────
  showRocco = false;
  roccoLine: { en: string; ar: string } = { en: '', ar: '' };
  private roccoIndex = 0;
  private roccoTimer: any = null;
  private readonly roccoLines: { en: string; ar: string }[] = [
    { en: "Good crack! The dial's listening — nudge it and try again.", ar: 'قرّبت! العجلة سامعاك — حرّكها وجرّب تاني.' },
    { en: 'Almost cracked it — feel for the click.', ar: 'قربت تفتحها — دوّر على الكليك.' },
    { en: 'Nice turn! Adjust a touch and set again.', ar: 'لفة حلوة! عدّل شوية واضبط تاني.' },
    { en: "You've got the combo — one more try.", ar: 'التركيبة قربت — جرّب مرة كمان.' }
  ];

  private prevRound = -1;
  private pendingTimeouts: any[] = [];
  private idleTimer: any = null;

  constructor(
    private miniGameService: MiniGameService,
    private gameAudioService: GameAudioService,
    private globalService: GlobalService
  ) {
    super();
  }

  ngOnInit(): void {
    this.globalService.getLang()
      .pipe(takeUntil(this.destroy$))
      .subscribe(lang => this.currLang = lang);

    this.miniGameService.session$
      .pipe(takeUntil(this.destroy$))
      .subscribe(s => {
        this.session = s;
        const round = this.state?.currentRound ?? -1;
        if (round !== this.prevRound) {
          this.prevRound = round;
          this.resetRoundUi();
        }
      });

    this.miniGameService.lastResult$
      .pipe(takeUntil(this.destroy$))
      .subscribe(r => { if (r) this.lastXp = r.xpEarned; });
  }

  ngOnDestroy(): void {
    this.pendingTimeouts.forEach(t => clearTimeout(t));
    this.pendingTimeouts = [];
    this.clearIdleTimer();
    if (this.roccoTimer) { clearTimeout(this.roccoTimer); this.roccoTimer = null; }
    this.destroy$.next();
    this.destroy$.complete();
  }

  private resetRoundUi(): void {
    this.showSuccess = false;
    this.resolving = false;
    this.hintVisible = false;
    this.hintText = { en: '', ar: '' };
    this.prevGuess = null;
    this.fineBandLit = false;
    this.hideCoach();
    this.clearIdleTimer();
    if (this.student) {
      this.showTurnCard = true;
      this.later(() => this.dismissTurnCard(), 1900);
    } else {
      this.showTurnCard = false;
      this.beginRoundTiming();
      this.startIdleTimer();
    }
  }

  dismissTurnCard(): void {
    if (!this.showTurnCard) return; // idempotent: manual tap + auto-timeout
    this.showTurnCard = false;
    this.hideCoach();
    this.beginRoundTiming();
    this.startIdleTimer();
  }

  private later(fn: () => void, ms: number): void {
    this.pendingTimeouts.push(setTimeout(fn, ms));
  }

  private startIdleTimer(): void {
    this.clearIdleTimer();
    this.idleTimer = setTimeout(() => {
      if (!this.hintVisible && !this.resolving) this.revealHint(null);
    }, 15000);
  }
  private clearIdleTimer(): void {
    if (this.idleTimer) { clearTimeout(this.idleTimer); this.idleTimer = null; }
  }

  // ── Derived state ──────────────────────────────────────────────────
  get state(): VaultCrackerState | null {
    return (this.session?.gameState as VaultCrackerState) ?? null;
  }
  get round(): VaultCrackerRound | null {
    const s = this.state;
    if (!s) return null;
    return s.rounds[s.currentRound] ?? null;
  }
  get currentRound(): number { return this.state?.currentRound ?? 0; }
  get totalRounds(): number { return this.state?.totalRounds ?? 0; }
  get roundDots(): number[] { return Array.from({ length: this.totalRounds }, (_, i) => i); }
  get solvedFlags(): boolean[] { return this.state?.solved ?? []; }

  get student(): MiniGameStudent | null {
    const s = this.state;
    if (!s || s.currentStudentId == null) return null;
    return this.session?.students.find(st => st.id === s.currentStudentId) ?? null;
  }
  get studentName(): string {
    const st = this.student;
    if (!st) return '';
    return (this.currLang === 'ar' ? st.name.ar : st.name.en || '').split(' ')[0];
  }

  // ── Prompt header (isolated LTR math run; Arabic-Indic digits) ────────
  dig(n: number): string {
    const s = String(n);
    if (this.currLang !== 'ar') return s;
    const map = ['٠', '١', '٢', '٣', '٤', '٥', '٦', '٧', '٨', '٩'];
    return s.replace(/\d/g, d => map[+d]);
  }
  /** The known part shown in the prompt (findWhole / findPercent numerator). */
  get promptPart(): number {
    const r = this.round;
    if (!r) return 0;
    if (r.subSkill === 'findWhole') return Math.round((r.percent / 100) * r.whole);
    if (r.subSkill === 'findPercent') return Math.round((r.answer / 100) * r.whole);
    return 0;
  }
  get promptText(): string {
    const r = this.round;
    if (!r) return '';
    const ar = this.currLang === 'ar';
    const p = this.dig(r.percent);
    const w = this.dig(r.whole);
    // Localize the WORDS + punctuation; digits/operators stay in the isolated LTR run.
    const pct = ar ? '٪' : '%';
    const of = ar ? 'من' : 'of';
    const q = ar ? '؟' : '?';
    if (r.subSkill === 'findWhole') return `${this.dig(this.promptPart)} = ${p}${pct} ${of} ${q}`;
    if (r.subSkill === 'findPercent') return `${this.dig(this.promptPart)} ${ar ? 'من أصل' : 'out of'} ${w} = ${ar ? '؟٪' : '?%'}`;
    return `${p}${pct} ${of} ${w} = ${q}`;
  }

  // ── SET / outcomes ─────────────────────────────────────────────────
  onSet(): void {
    if (this.resolving || this.showTurnCard || !this.stage?.canCommit) return;
    this.hideCoach();
    this.markInteraction();       // first SET = first submission (reaction time)
    this.clearIdleTimer();
    this.gameAudioService.play('assets/game-mode/sound-effects/fastSoundEffect.mp3');
    this.stage.commit();
  }

  onSolved(committed: number): void {
    if (this.resolving) return;
    this.resolving = true;
    this.hideCoach();
    const st = this.state;
    if (st) {
      const step = this.round?.tickStep ?? 1;
      if (this.failedTries === 0) st.solveType = 'clean';
      else if (this.prevGuess != null && this.fineBandLit && Math.abs(committed - this.prevGuess) === step) st.solveType = 'walked';
      else st.solveType = 'reasoned-retry';
    }
    this.gameAudioService.play('assets/game-mode/sound-effects/correctSoundEffect.mp3');
    this.miniGameService.scoreVaultCrackerRound(true, this.roundMeta(true));
    this.showSuccess = true;
    this.later(() => this.miniGameService.advanceVaultCrackerRound(), 3200);
  }

  onMissed(ev: { committed: number; band: 'green' | 'amber' | 'grey'; fine: boolean }): void {
    if (this.resolving) return;
    // A wrong SET is NOT the end — the dial stays live to retry. NO buzzer (§3.2).
    this.markFailedTry();
    const trap = this.classifyTrap(ev.committed);
    const st = this.state;
    if (st) st.lastTrapHit = trap;
    this.prevGuess = ev.committed;
    if (ev.fine) this.fineBandLit = true;
    this.revealHint(trap);        // fraction ring + scaffolded / trap-specific text
    this.showCoach();             // Rocco encouragement (no answer)
  }

  skip(): void {
    if (this.resolving) return;
    this.resolving = true;
    const st = this.state;
    if (st) st.solveType = null;  // skipped → walked-away
    this.miniGameService.scoreVaultCrackerRound(false, this.roundMeta(false));
    this.later(() => this.miniGameService.advanceVaultCrackerRound(), 250);
  }

  // ── Misconception trap classifier (kebab slugs for analytics) ─────────
  private classifyTrap(committed: number): string | null {
    const r = this.round;
    if (!r) return null;
    const a = r.answer;

    if (committed === r.percent && r.subSkill !== 'findPercent') return 'percent-as-part';

    if (r.subSkill === 'findWhole') {
      const part = Math.round((r.percent / 100) * r.whole);
      if (committed === Math.round((r.percent / 100) * part)) return 'reverse-took-percent-of-part';
      if (committed === r.whole - a) return 'reverse-other-portion';
    }
    if (r.subSkill === 'findPercent') {
      const part = Math.round((a / 100) * r.whole);
      if (committed === part) return 'findpercent-dialed-part';
      if (committed === 100 - a) return 'findpercent-remainder';
    }

    if (committed === r.whole - a) return 'part-vs-remainder';
    if (committed === a * 10 || committed === a / 10) return 'place-value';

    const benches = [0.1, 0.25, 0.5, 0.75].map(f => Math.round(f * r.whole));
    if (benches.includes(committed)) return 'wrong-benchmark';
    return null;
  }

  // ── Hint text (scaffolded, answer-preserving) ─────────────────────────
  private revealHint(trap: string | null): void {
    this.hintVisible = true;
    const st = this.state;
    if (st) st.usedContentHint = true;
    this.hintText = trap ? this.trapNudge(trap) : this.contentHint();
  }

  private contentHint(): { en: string; ar: string } {
    const r = this.round;
    if (!r) return { en: '', ar: '' };
    if (r.subSkill === 'bridge') {
      return {
        en: 'This percent and this fraction are the same amount. Take that fraction OF the vault.',
        ar: 'النسبة دي والكسر ده نفس المقدار. خُد الكسر ده من الخزنة.'
      };
    }
    if (r.subSkill === 'findWhole') {
      return {
        en: 'The gems you know are only part of the vault. If that part is one quarter, the whole is four of them. How many parts make 100%?',
        ar: 'الجواهر اللي معاك جزء بس. لو الجزء ربع، تبقى الخزنة أربعة منه. كام جزء يعمل ١٠٠٪؟'
      };
    }
    if (r.subSkill === 'findPercent') {
      return {
        en: 'Write the part over the whole, then rename it out of 100. part / whole = ? / 100.',
        ar: 'اكتب الجزء على الكل، وبعدين حوّله لكل ١٠٠. الجزء ÷ الكل = ؟ ÷ ١٠٠.'
      };
    }
    // partOf — benchmark variant for 10/25/50, else the 10%-anchor scaffold.
    if (r.percent === 10 || r.percent === 25 || r.percent === 50) {
      return {
        en: '50% is one half · 25% is one quarter · 10% is one tenth — take that part of the vault.',
        ar: '٥٠٪ نصف · ٢٥٪ رُبع · ١٠٪ العُشر — خُد الجزء ده من الخزنة.'
      };
    }
    return {
      en: 'Find 10% first — move the decimal one place. Then: how many 10%s are in this percent?',
      ar: 'الأول لاقي ١٠٪ — حرّك العلامة العشرية خانة واحدة. بعدين: كام ١٠٪ في النسبة دي؟'
    };
  }

  private trapNudge(trap: string): { en: string; ar: string } {
    const nudges: Record<string, { en: string; ar: string }> = {
      'percent-as-part': {
        en: "That's the percent itself, not a gem count. 25% means 25 of every 100 — so how many out of this vault?",
        ar: 'دي النسبة نفسها، مش عدد الجواهر. ٢٥٪ يعني ٢٥ من كل ١٠٠ — يبقى كام من الخزنة؟'
      },
      'part-vs-remainder': {
        en: "That's the gems left in the vault, not the ones asked for. Dial the part the question wants.",
        ar: 'دي الجواهر الباقية في الخزنة، مش المطلوبة. اضبط الجزء اللي السؤال عايزه.'
      },
      'place-value': {
        en: 'Close digits! Check your place value — that answer is ten times off. Re-count the 10%s.',
        ar: 'الأرقام قريبة! راجع القيمة المكانية — الإجابة أكبر أو أصغر بعشر مرات. عُد الـ١٠٪ تاني.'
      },
      'wrong-benchmark': {
        en: 'That fits a different benchmark. Match the percent in the question to its fraction first.',
        ar: 'ده يناسب نسبة تانية. طابِق نسبة السؤال مع كسرها الأول.'
      },
      'reverse-took-percent-of-part': {
        en: 'You took the percent of the small part. Here the part IS the percent of the whole — grow it back to 100%.',
        ar: 'انت خدت النسبة من الجزء الصغير. هنا الجزء هو نسبة من الكل — كبّره لحد ١٠٠٪.'
      },
      'reverse-other-portion': {
        en: "That's the leftover slice of the vault. You need the FULL vault — how many parts make 100%?",
        ar: 'ده الجزء الباقي من الخزنة. انت محتاج الخزنة كاملة — كام جزء يعمل ١٠٠٪؟'
      },
      'findpercent-dialed-part': {
        en: "That's the gem count, but the dial wants a PERCENT. Rename part / whole as ? out of 100.",
        ar: 'ده عدد الجواهر، بس العجلة عايزة نسبة. حوّل الجزء ÷ الكل لـ؟ من ١٠٠.'
      },
      'findpercent-remainder': {
        en: "That's the percent still LOCKED away. You want the percent you have — part over whole.",
        ar: 'دي النسبة اللي لسه مقفولة. انت عايز النسبة اللي معاك — الجزء على الكل.'
      }
    };
    return nudges[trap] ?? this.contentHint();
  }

  // ── Rocco coach (persistent element, opacity-toggled, no flicker) ─────
  private showCoach(): void {
    this.roccoLine = this.roccoLines[this.roccoIndex % this.roccoLines.length];
    this.roccoIndex++;
    this.showRocco = true;
    if (this.roccoTimer) clearTimeout(this.roccoTimer);
    this.roccoTimer = setTimeout(() => { this.showRocco = false; }, 2000);
  }
  private hideCoach(): void {
    if (this.roccoTimer) { clearTimeout(this.roccoTimer); this.roccoTimer = null; }
    this.showRocco = false;
  }
}
